import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import SuggestedTrade from "../SuggestedTrade/SuggestedTrade";
import QueuedTrades from "../QueuedTrades/QueuedTrades";
import Balance from "../Balance/Balance";
import PositionsComponent from "../Positions/Positions";
import WheelConfigPanel from "../WheelConfigPanel/WheelConfigPanel";
import SavvyStatus from "../SavvyStatus/SavvyStatus";
import SimulatePanel from "../SimulatePanel/SimulatePanel";
import ExecutionAnalysis from "../ExecutionAnalysis/ExecutionAnalysis";
import { buildApiUrl } from "../../lib/api";
import "./Dashboard.css";

const DEFAULT_STRATEGY = localStorage.getItem("tradeStrategy") || "wheel";
const DEFAULT_ACCOUNT_SOURCE = localStorage.getItem("accountSource") || "trader";
const STRATEGY_STATUS_LABELS = {
    wheel: "Wheel active",
    tactical_spread: "Tactical spread active",
    regime_spread: "Regime spread active",
    earnings: "Earnings active",
};
const ACCOUNT_SOURCE_OPTIONS = [
    {
        id: "trader",
        label: "Trader Account",
        caption: "Keep using the Alpaca trading account for paper/live iteration.",
    },
    {
        id: "broker",
        label: "Broker Account",
        caption: "Use the customer broker account once broker auth is healthy.",
    },
];

const bodyVariants = {
    hidden:  { opacity: 0, height: 0 },
    visible: { opacity: 1, height: "auto", transition: { duration: 0.22, ease: "easeOut" } },
    exit:    { opacity: 0, height: 0,      transition: { duration: 0.18, ease: "easeIn"  } },
};

function loadRecentTickers() {
    try {
        return JSON.parse(localStorage.getItem("recentTickers") || "[]").slice(0, 5);
    } catch {
        return [];
    }
}

function fmtUSD(n) {
    return Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function ChevronIcon({ open }) {
    return (
        <svg
            className={`dash-chevron ${open ? "dash-chevron--open" : ""}`}
            width="14" height="14" viewBox="0 0 14 14" fill="none"
        >
            <path d="M2.5 5L7 9.5L11.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
}

function CollapsibleCard({ title, open, onToggle, children }) {
    return (
        <div className="dash-card">
            <button className="dash-card-header" onClick={onToggle} type="button">
                <span className="dash-card-title">{title}</span>
                <ChevronIcon open={open} />
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        className="dash-card-body"
                        variants={bodyVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        style={{ overflow: "hidden" }}
                    >
                        <div className="dash-card-inner">
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function AccountSourcePanel({ accountSource, onChange }) {
    return (
        <section className="dash-account-source">
            <div className="dash-account-source-copy">
                <span className="dash-account-source-kicker">Account source</span>
                <h3>{accountSource === "broker" ? "Broker account mode" : "Trader account mode"}</h3>
                <p>
                    {accountSource === "broker"
                        ? "Use the broker customer account when broker authentication is available."
                        : "Use the Alpaca trading account so you can keep paper or live strategy work moving."}
                </p>
            </div>
            <div className="dash-account-source-toggle" role="tablist" aria-label="Account source">
                {ACCOUNT_SOURCE_OPTIONS.map((option) => (
                    <button
                        key={option.id}
                        type="button"
                        className={`dash-account-source-option ${accountSource === option.id ? "active" : ""}`}
                        onClick={() => onChange(option.id)}
                    >
                        <span>{option.label}</span>
                        <small>{option.caption}</small>
                    </button>
                ))}
            </div>
        </section>
    );
}

const Dashboard = ({ isLocalDevMode = false }) => {
    const [revealed] = useState(true);

    const sec = (delay) => ({
        initial: false,
        animate: revealed ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 },
        transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1], delay },
        style: { width: "100%" },
    });
    const [activeTicker,     setActiveTicker]     = useState(localStorage.getItem("lastTicker") || "AAPL");
    const [tickerInput,      setTickerInput]      = useState("");
    const [recentTickers,    setRecentTickers]    = useState(loadRecentTickers);
    const [showConfig,       setShowConfig]       = useState(false);
    const [portfolioConfig,  setPortfolioConfig]  = useState({ strategy: DEFAULT_STRATEGY });
    const [accountSource,    setAccountSource]    = useState(DEFAULT_ACCOUNT_SOURCE);
    const [portfolioValue,   setPortfolioValue]   = useState(null);
    const [buyingPower,      setBuyingPower]      = useState(null);
    const [dailyPnL,         setDailyPnL]         = useState(null);
    const [strategyStatus,   setStrategyStatus]   = useState(null);
    const [queueCount,       setQueueCount]       = useState(0);
    const [committedCapital, setCommittedCapital] = useState(0);
    const [marketOpen,       setMarketOpen]       = useState(null);
    const [tradeRefreshKey,  setTradeRefreshKey]  = useState(0);
    const [simulationLaunch, setSimulationLaunch] = useState(null);

    const [tradeOpen,    setTradeOpen]    = useState(true);
    const [simulateOpen, setSimulateOpen] = useState(false);
    const [positionsOpen, setPositionsOpen] = useState(false);
    const [analysisOpen, setAnalysisOpen] = useState(false);
    const configViewRef = useRef(null);
    const simulateCardRef = useRef(null);

    useEffect(() => {
        localStorage.setItem("tradeStrategy", portfolioConfig.strategy);
        setStrategyStatus(STRATEGY_STATUS_LABELS[portfolioConfig.strategy] || "Strategy active");
    }, [portfolioConfig.strategy]);

    useEffect(() => {
        localStorage.setItem("accountSource", accountSource);
        if (accountSource !== "trader") {
            setQueueCount(0);
            setCommittedCapital(0);
        }
    }, [accountSource]);

    useEffect(() => {
        if (!showConfig) return;
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        window.scrollTo(0, 0);
    }, [showConfig]);

    const openSimulation = useCallback((launch = null) => {
        setSimulateOpen(true);
        if (launch) {
            setSimulationLaunch({ ...launch, token: Date.now() });
        }
        window.requestAnimationFrame(() => {
            simulateCardRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        });
    }, []);

    const fetchAccountSummary = useCallback(async () => {
        try {
            const { data } = await axios.get(buildApiUrl("/balance/current"), {
                params: { account_source: accountSource },
                withCredentials: true,
            });
            if (data?.length > 0) {
                const snapshot = data[0];
                if (snapshot?.source === "broker_unavailable") {
                    setPortfolioValue(0);
                    setBuyingPower(0);
                    setDailyPnL(0);
                    return;
                }
                setPortfolioValue(snapshot.portfolio_value);
                setBuyingPower(snapshot.buying_power);
                setDailyPnL(snapshot.profit_loss ?? 0);
                if (snapshot.buying_power != null) {
                    localStorage.setItem("savvy_buying_power", String(snapshot.buying_power));
                }
            }
        } catch {
            // non-blocking
        }
    }, [accountSource]);

    const fetchMarketStatus = useCallback(async () => {
        try {
            const { data } = await axios.get(buildApiUrl("/market-clock/"), { withCredentials: true });
            setMarketOpen(data?.is_open ?? null);
        } catch {
            // non-blocking
        }
    }, []);

    const retryQueuedOrders = useCallback(async () => {
        try {
            await axios.post(buildApiUrl("/retry-queued"), {}, { withCredentials: true });
        } catch {
            // non-blocking
        }
    }, []);

    useEffect(() => {
        fetchAccountSummary();
        fetchMarketStatus();
        if (accountSource === "trader") {
            retryQueuedOrders();
        }
    }, [accountSource, fetchAccountSummary, fetchMarketStatus, retryQueuedOrders]);


    useEffect(() => {
        const intervalId = window.setInterval(() => {
            fetchMarketStatus();
        }, 60_000);

        const handleVisibility = () => {
            if (document.visibilityState === "visible") {
                fetchMarketStatus();
            }
        };

        document.addEventListener("visibilitychange", handleVisibility);
        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, [fetchMarketStatus]);

    const applyTicker = (ticker) => {
        const t = ticker.trim().toUpperCase();
        if (!t) return;
        localStorage.setItem("lastTicker", t);
        setActiveTicker(t);
        setTickerInput("");
        setShowConfig(false);
        setRecentTickers((prev) => {
            const next = [t, ...prev.filter((r) => r !== t)].slice(0, 5);
            localStorage.setItem("recentTickers", JSON.stringify(next));
            return next;
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") applyTicker(tickerInput);
    };

    // Savvy insight bullets
    const utilPct = portfolioValue && committedCapital
        ? Math.min(100, (committedCapital / portfolioValue) * 100) : 0;
    const availableCapital = portfolioValue != null
        ? Math.max(0, (portfolioValue ?? 0) - (committedCapital ?? 0)) : null;
    const insightBullets = [];
    if (marketOpen === false)
        insightBullets.push("Market is closed — queued trades will execute at open.");
    if (accountSource === "trader" && utilPct >= 60)
        insightBullets.push(`${utilPct.toFixed(0)}% of portfolio is committed. Consider diversifying.`);
    else if (accountSource === "trader" && utilPct >= 30)
        insightBullets.push(`${utilPct.toFixed(0)}% of portfolio committed across queued trades.`);
    if (accountSource === "trader" && queueCount > 0)
        insightBullets.push(`${queueCount} trade${queueCount > 1 ? "s" : ""} waiting in queue.`);
    if (availableCapital != null && portfolioValue)
        insightBullets.push(`$${fmtUSD(availableCapital)} available for new positions.`);
    if (accountSource === "broker")
        insightBullets.push("Broker mode is isolated from trader-account orders while broker setup is in progress.");
    if (insightBullets.length === 0)
        insightBullets.push("No active concerns. Portfolio looks clean.");

    return (
        <>
            {showConfig ? (
                <div
                    ref={configViewRef}
                    className="dashboard dashboard--config"
                >
                    <WheelConfigPanel
                        symbol={activeTicker}
                        onBack={() => setShowConfig(false)}
                        portfolioValue={portfolioValue}
                        buyingPower={buyingPower}
                        strategy={portfolioConfig.strategy}
                        accountSource={accountSource}
                        onStrategyChange={(strategy) => setPortfolioConfig((prev) => ({ ...prev, strategy }))}
                    />
                </div>
            ) : (
                <div className="dashboard">

                    {/* 1. Market status */}
                    <motion.div {...sec(0)}>
                        <SavvyStatus
                            queueCount={queueCount}
                            portfolioValue={portfolioValue}
                            committedCapital={committedCapital}
                            marketOpen={marketOpen}
                        />
                    </motion.div>

                    {/* 2. Portfolio Performance */}
                    <motion.div {...sec(0.06)}>
                        <AccountSourcePanel
                            accountSource={accountSource}
                            onChange={setAccountSource}
                        />
                    </motion.div>

                    <motion.section className="dash-portfolio-section" {...sec(0.12)}>
                        <Balance accountSource={accountSource} revealed={revealed} />
                    </motion.section>

                    {/* 3. Trade card */}
                    <motion.div {...sec(0.22)}>
                    <CollapsibleCard
                        title="Trade"
                        open={tradeOpen}
                        onToggle={() => setTradeOpen((o) => !o)}
                    >
                        {/* Search symbol */}
                        <section className="dashboard-search-section">
                            <span className="dashboard-search-label">Search symbol</span>
                            <div className="dashboard-search-bar">
                                <svg className="dashboard-search-icon" viewBox="0 0 16 16" fill="none">
                                    <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.25"/>
                                    <path d="M10 10L14 14" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round"/>
                                </svg>
                                <input
                                    className="dashboard-search-input"
                                    type="text"
                                    value={tickerInput}
                                    onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                                    onKeyDown={handleKeyDown}
                                    placeholder="AAPL, MSFT, NVDA..."
                                    maxLength={5}
                                    autoComplete="off"
                                    spellCheck={false}
                                />
                                <AnimatePresence>
                                    {tickerInput && (
                                        <motion.span
                                            className="dashboard-search-hint"
                                            initial={{ opacity: 0, x: 4 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 4 }}
                                            transition={{ duration: 0.12 }}
                                        >
                                            ↵ Enter
                                        </motion.span>
                                    )}
                                </AnimatePresence>
                            </div>
                            {recentTickers.length > 0 && (
                                <div className="dashboard-recent-row">
                                    {recentTickers.map((t) => (
                                        <motion.button
                                            key={t}
                                            className={`dashboard-recent-chip ${t === activeTicker ? "active" : ""}`}
                                            onClick={() => applyTicker(t)}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.96 }}
                                            transition={{ duration: 0.12 }}
                                        >
                                            {t}
                                        </motion.button>
                                    ))}
                                </div>
                            )}
                        </section>

                        {/* Trading card */}
                        <SuggestedTrade
                            symbol={activeTicker}
                            portfolioValue={portfolioValue}
                            buyingPower={buyingPower}
                            dailyPnL={dailyPnL}
                            strategyStatus={strategyStatus}
                            portfolioConfig={portfolioConfig}
                            accountSource={accountSource}
                            onConfigure={() => { document.documentElement.scrollTop = 0; document.body.scrollTop = 0; setShowConfig(true); }}
                            onOpenSimulation={openSimulation}
                            onSelectSymbol={applyTicker}
                            hasQueuedOrders={accountSource === "trader" && queueCount > 0}
                            onTradeExecuted={() => setTradeRefreshKey(k => k + 1)}
                            onStrategyChange={(strategy) => setPortfolioConfig((prev) => ({ ...prev, strategy }))}
                        />

                        {accountSource === "trader" ? (
                            <QueuedTrades
                                portfolioValue={portfolioValue}
                                onCountChange={setQueueCount}
                                onCapitalChange={setCommittedCapital}
                                refreshKey={tradeRefreshKey}
                            />
                        ) : null}

                        {/* Savvy Insight */}
                        <div className="dash-insight">
                            <div className="dash-insight-title">Savvy Insight</div>
                            {insightBullets.map((b, i) => (
                                <div key={i} className="dash-insight-bullet">· {b}</div>
                            ))}
                        </div>
                    </CollapsibleCard>
                    </motion.div>

                    {/* 4. Simulate Strategy */}
                    <motion.div ref={simulateCardRef} {...sec(0.30)}>
                        <CollapsibleCard
                            title="Simulate Strategy"
                            open={simulateOpen}
                            onToggle={() => setSimulateOpen((o) => !o)}
                        >
                            <SimulatePanel
                                defaultBuyingPower={buyingPower}
                                symbol={activeTicker}
                                launchRequest={simulationLaunch}
                            />
                        </CollapsibleCard>
                    </motion.div>

                    {/* 5. Positions */}
                    <motion.div {...sec(0.36)}>
                        <CollapsibleCard
                            title="Positions"
                            open={positionsOpen}
                            onToggle={() => setPositionsOpen((o) => !o)}
                        >
                            <PositionsComponent refreshKey={tradeRefreshKey} accountSource={accountSource} />
                        </CollapsibleCard>
                    </motion.div>

                    {/* 6. Execution Analysis */}
                    <motion.div {...sec(0.42)}>
                        <CollapsibleCard
                            title="Execution Analysis"
                            open={analysisOpen}
                            onToggle={() => setAnalysisOpen((o) => !o)}
                        >
                            {analysisOpen && <ExecutionAnalysis />}
                        </CollapsibleCard>
                    </motion.div>
                </div>
            )}
        </>
    );
};

export default Dashboard;
